import { useEffect, useRef, useState, useCallback } from "react";
import { Box, Card } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import BuildIdEvent, { BuildIdEventListener } from "../../common/BuildIdEvent";
import SignalR from "../../common/SignalR";
import { HubConnectionState } from "@microsoft/signalr";
import Message from "../../common/Messages";
import ChatLoader from "../../common/ChatLoader";
import {
  AI_TRANSLATE,
  GET_CHAT_GROUP_MESSAGES_BY_CHATGROUP_ID,
  LEAVE_CHAT_GROUP
} from "../../constant/Apipath";
import { Postrequestcall } from "../../request/Postrequest";
import { Getrequestcall } from "../../request/Getrequest";
import { db } from "../../db"; // ✅ IndexedDB
import moment from "moment";
import { setSelectedChat } from "../../redux/actions/actions";

const TAG = "chat";
const MESSAGES_PER_PAGE = 50;
const INITIAL_PAGE = 1;

const ChatMessages = ({ messageLoader, setMessageLoader, isGroupListLoading }) => {
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const scrollMode = useRef("none");
  const isFirstRender = useRef(true);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
  const [hasMore, setHasMore] = useState(true);

  const { loginData, chatState, selectedChat } = useSelector((state) => state?.main);
  const dispatch = useDispatch();

  const buildIdEvent = BuildIdEvent();

  const languageChangeRef = useRef(loginData?.ChatLanguage || "en");

  useEffect(() => {
    languageChangeRef.current = loginData?.ChatLanguage || "en";
  }, [loginData?.ChatLanguage]);

  /** ---------------------- Auto-scroll ---------------------- */
  useEffect(() => {
    if (loading || messageLoader || isGroupListLoading) return;
    if (!messagesEndRef.current) return;
    if (scrollMode.current === "instant") {
      // Jump directly (like WhatsApp when opening chat)
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
      scrollMode.current = "none";
    } else if (scrollMode.current === "smooth") {
      // Smooth scroll for new message
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      scrollMode.current = "none";
    }
  }, [messages, loading, messageLoader, isGroupListLoading]);

  /** ---------------------- Init ---------------------- */
  useEffect(() => {
    initializeListener();
    getMessagesData();
  }, [selectedChat?.chatGroupId]);

  /** ---------------------- SignalR Setup ---------------------- */
  const initializeListener = () => {
    SignalR.connectionStatusListener((status) => {
      if (status === HubConnectionState.Connected) {
        setListener();
        processChatGroupDetails(selectedChat?.chatGroupId);
      } else {
        console.warn("initializeListener-> SignalR not connected yet.");
      }
    }, TAG);
  };

  const setListener = () => {
    BuildIdEventListener.onNewChatMessage((data) => {
      const receivedNewMessage = data[0];
      console.log("ChatMessages-> New chat message received:", receivedNewMessage);
      if (receivedNewMessage.toChatGroupId == selectedChat?.chatGroupId) {
        appendMessage(receivedNewMessage);
      }
    }, TAG);
  };

  const appendMessage = async (obj) => {
    try {
      // Check if this is the special delete message
      if (obj.messageText === "a3f38d6a-c85e-49a9-a9d5-6f9de8c8559d") {
        // Check if this is a single chat (2 users only)
        if (selectedChat?.nrOfUsersInGroup === 2) {
          return;
        }
      }

      scrollMode.current = "smooth";
      const condition =
        languageChangeRef.current !== obj.messageLanguage && obj.messageTypeId === 1;
      if (condition) {
        const payload = { From: obj.messageLanguage, To: selectedLanguage, Text: obj.messageText };
        const resp = await Postrequestcall(AI_TRANSLATE, payload, loginData?.accessToken);
        obj.translatedMessage = resp?.data?.data;
      }
      const lastMessage = await db.messages
        .where("chatGroupId")
        .equals(selectedChat?.chatGroupId)
        .last();
      await db.messages.put({
        id: obj?.id || 0,
        chatGroupId: obj?.toChatGroupId || selectedChat?.chatGroupId,
        messageText: obj?.messageText || "",
        messageTypeId: obj?.messageTypeId || 1,
        duration: obj?.duration || 0,
        dateSent: obj?.dateSent || new Date().toISOString(),
        fromUserId: obj?.fromUserId || 0,
        fromFirstName: obj?.fromFirstName,
        translatedMessage: obj?.translatedMessage ?? obj?.messageText,
        isAdjacentMsg: getAdjacentMessages(obj, lastMessage)
      });
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== obj.id);
        return [...filtered, obj].sort((a, b) => a.id - b.id);
      });
    } catch (err) {
      console.log("appendMessage-> Error:", err);
    }
  };

  const processChatGroupDetails = async (chatGroupId) => {
    try {
      await buildIdEvent.sendChatGroupDetails(chatGroupId, false);
    } catch (err) {
      console.error("processChatGroupDetails-> Error: ", err);
    }
  };

  const getMessagesFromDB = async (chatGroupId, page, pageSize) => {
    try {
      const offset = (page - 1) * pageSize;
      const dbMessages = await db.messages
        .where("chatGroupId")
        .equals(chatGroupId)
        .reverse()
        .offset(offset)
        .limit(pageSize)
        .toArray();
      const unique = Array.from(new Map(dbMessages.map((m) => [m.id, m])).values());
      return unique.sort((a, b) => a.id - b.id);
    } catch (err) {
      console.error("Error fetching messages from IndexedDB:", err);
      return [];
    }
  };

  // Get the last message ID from IndexedDB for a chat group
  const getLastMessageIdFromDB = async (chatGroupId) => {
    const lastMsg = await db.messages.where("chatGroupId").equals(chatGroupId).last();
    // console.log("Last Message from DB: ", lastMsg);
    return lastMsg ? lastMsg?.id : 0;
  };

  const getMessagesData = async () => {
    setLoading(true);
    setHasMore(true);
    setCurrentPage(INITIAL_PAGE);
    const chatGroupId = selectedChat?.chatGroupId;
    try {
      if (!chatGroupId) {
        setMessages([]);
        setLoading(false);
        return;
      }
      const lastId = await getLastMessageIdFromDB(selectedChat?.chatGroupId);
      console.log("ChatMessages-> Last Message ID in DB:", lastId);
      scrollMode.current = "instant";
      if (lastId === 0) {
        // Get all messages only if no local data empty first time
        const getUrl = `${GET_CHAT_GROUP_MESSAGES_BY_CHATGROUP_ID}/${Number(loginData?.userInformation?.UserId)}/${0}/${Number(chatGroupId)}/${loginData?.ChatLanguage}/${loginData?.TranslateMessage}`;
        const res = await Getrequestcall(getUrl, loginData?.accessToken);
        if (res.status === 200) {
          const apiMessages = res?.data?.data || [];
          setHasMore(false);
          if (apiMessages?.length > 0) {
            await mergeMessagesToDB(
              chatGroupId,
              apiMessages.map((msg, index) => ({
                id: msg?.id || 0,
                chatGroupId,
                messageText: msg?.messageText || "",
                messageTypeId: msg?.messageTypeId || 1,
                dateSent: msg?.dateSent || new Date().toISOString(),
                fromUserId: msg?.fromUserId || 0,
                translatedMessage: msg?.translatedMessage
                  ? msg?.translatedMessage
                  : msg?.messageText,
                fromFirstName: msg?.fromFirstName,
                isAdjacentMsg: getAdjacentMessages(msg, apiMessages[index - 1])
              }))
            );
          } else {
            setMessages([]);
          }
        } else {
          setMessages([]);
        }
      } else {
        // If lastId is not zero, it means we have local data, so fetch only new messages passing lastId
        const getUrl = `${GET_CHAT_GROUP_MESSAGES_BY_CHATGROUP_ID}/${Number(loginData?.userInformation?.UserId)}/${lastId}/${Number(chatGroupId)}/${loginData?.ChatLanguage}/${loginData?.TranslateMessage}`;
        const res = await Getrequestcall(getUrl, loginData?.accessToken);
        if (res.status === 200) {
          const apiMessages = res?.data?.data || [];
          if (apiMessages?.length > 0) {
            setHasMore(false);
            console.log("New messages from API:", apiMessages.length);
            await mergeMessagesToDB(
              chatGroupId,
              apiMessages.map((msg, index) => ({
                id: msg?.id || 0,
                chatGroupId,
                messageText: msg?.messageText || "",
                messageTypeId: msg?.messageTypeId || 1,
                dateSent: msg?.dateSent || new Date().toISOString(),
                fromUserId: msg?.fromUserId || 0,
                translatedMessage: msg?.translatedMessage
                  ? msg?.translatedMessage
                  : msg?.messageText,
                fromFirstName: msg?.fromFirstName,
                isAdjacentMsg: getAdjacentMessages(msg, apiMessages[index - 1])
              }))
            );
          } else {
            // No new messages from API show local data
            const localMessages = await getMessagesFromDB(
              chatGroupId,
              INITIAL_PAGE,
              MESSAGES_PER_PAGE
            );
            if (localMessages?.length > 0) {
              setMessages(localMessages);
            } else {
              setMessages([]);
            }
          }
        } else {
          try {
            const localMessages = await getMessagesFromDB(
              chatGroupId,
              INITIAL_PAGE,
              MESSAGES_PER_PAGE
            );
            if (localMessages?.length > 0) {
              setMessages(localMessages);
            } else {
              setMessages([]);
            }
          } catch (error) {
            setMessages([]);
          }
        }
      }
    } catch (err) {
      try {
        const localMessages = await getMessagesFromDB(chatGroupId, INITIAL_PAGE, MESSAGES_PER_PAGE);
        if (localMessages?.length > 0) {
          setMessages(localMessages);
        } else {
          setMessages([]);
        }
      } catch (error) {
        setMessages([]);
      }
    } finally {
      setLoading(false);
      setMessageLoader(false);
    }
  };

  const getAdjacentMessages = (receivedMessage, previousMessage = {}) => {
    const currentDate = moment();
    const { dateSent, fromUserId } = receivedMessage;
    const { dateSent: prevDateSent = currentDate, fromUserId: prevFromUserId = 0 } =
      previousMessage;
    const timeDiff = moment(dateSent).diff(moment(prevDateSent), "minute");
    const isAdjacent = timeDiff < 5 && fromUserId === prevFromUserId;
    return isAdjacent;
  };

  const mergeMessagesToDB = async (chatGroupId, newMessages) => {
    if (!Array.isArray(newMessages) || newMessages.length === 0) return;
    const existing = await db.messages.where("chatGroupId").equals(chatGroupId).toArray();
    const all = [...existing, ...newMessages];
    const unique = Array.from(new Map(all.map((m) => [m.id, m])).values());
    await db.messages.bulkPut(unique);
    setMessages(unique.sort((a, b) => a.id - b.id));
  };

  /** ---------------------- Scroll for infinite load ---------------------- */
  const handleScroll = useCallback(
    (e) => {
      const container = e.target;
      const threshold = 20; // px from top
      if (
        container.scrollTop <= threshold &&
        !loadingMore &&
        !loading &&
        hasMore && // only if more to load
        currentPage > 0 // optional extra safety
      ) {
        loadMoreMessages();
      }
    },
    [loadingMore, loading, currentPage, hasMore]
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  /** ---------------------- API & DB Loader ---------------------- */
  const loadMoreMessages = async () => {
    // console.log("Loading more messages...", !hasMore || loadingMore);
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    // console.log("loadMoreMessages-> Loading more messages...");
    try {
      const container = scrollContainerRef.current;
      const oldScrollHeight = container.scrollHeight;
      scrollMode.current = "none"; // ✅ don't auto scroll when loading more
      const nextPage = currentPage + 1;
      const localMessages = await getMessagesFromDB(
        selectedChat?.chatGroupId,
        nextPage,
        MESSAGES_PER_PAGE
      );
      if (localMessages.length !== 0) {
        // Next 100 local messages loaded,
        if (localMessages?.length < MESSAGES_PER_PAGE) {
          setHasMore(false);
        }
        setMessages((prev) => [...localMessages, ...prev]);
        setCurrentPage(nextPage);
      }
      setTimeout(() => {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - oldScrollHeight;
      }, 0);
    } catch (err) {
      console.error("loadMoreMessages-> Error:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  /** ---------------------- UI ---------------------- */
  return (
    <Card
      ref={scrollContainerRef}
      sx={{ overflowY: "auto", height: "100%", p: 2, borderRadius: "0px" }}>
      {loading || messageLoader || isGroupListLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <ChatLoader />
        </Box>
      ) : (
        <Message messages={messages} selectedChat={selectedChat} loadingMore={loadingMore} />
      )}
      <div ref={messagesEndRef} />
    </Card>
  );
};

export default ChatMessages;
